import { RENTAL_PORTAL_MOCK_STATE } from '../../constants/rentalPortalMockState'
import { hasRentalPortalMockState } from '../../utils/rentalPortalMockState'

const MOCK_MATCH_RULES_CRITERIA = [
  {
    gates_eligibility: true,
    key: 'household',
    listing_value: 'max 4 · min — persons',
    measure_detail:
      'The applicant’s household size against the unit’s maximum occupancy. It gates eligibility and also carries a weight. Measurement is fixed in the company catalogue (Settings → Matching).',
    weight: 20,
  },
  {
    gates_eligibility: true,
    key: 'pets',
    listing_value: 'Not allowed',
    measure_detail:
      'The applicant’s pets against the unit’s pet policy. It gates eligibility and carries no weight. Measurement is fixed in the company catalogue (Settings → Matching).',
    weight: 0,
  },
  {
    gates_eligibility: true,
    key: 'wbs',
    listing_value: 'not required',
    measure_detail:
      'Whether the applicant holds the certificate the unit requires. It gates eligibility and carries no weight. Measurement is fixed in the company catalogue (Settings → Matching).',
    weight: 0,
  },
  {
    gates_eligibility: true,
    key: 'income_min',
    listing_value: '3.0 × warm rent = € 3.750',
    measure_detail:
      'The applicant’s confirmed income against the multiple of the warm rent this unit asks for. It gates eligibility and carries no weight. Asked from Screening on.',
    weight: 0,
  },
  {
    gates_eligibility: false,
    key: 'move_in',
    listing_value: 'available 15/10/2026',
    measure_detail:
      'How close the applicant’s desired move-in is to the date the unit becomes available. It carries a weight and does not gate eligibility.',
    weight: 30,
  },
  {
    gates_eligibility: false,
    key: 'budget',
    listing_value: '€ 1.290 warm',
    measure_detail:
      'The applicant’s budget against the unit’s warm rent, with the catalogue tolerance. It carries a weight and does not gate eligibility on this listing.',
    weight: 25,
  },
  {
    gates_eligibility: false,
    key: 'needs',
    listing_value: 'balcony ✓ · lift ✓',
    measure_detail:
      'Which of the unit’s features the applicant asked for. It carries a weight and does not gate eligibility.',
    weight: 15,
  },
  {
    gates_eligibility: false,
    key: 'rooms_area',
    listing_value: '3 rooms · 88 m²',
    measure_detail:
      'The applicant’s room and area expectations against the unit. It carries a weight and does not gate eligibility.',
    weight: 5,
  },
  {
    gates_eligibility: false,
    key: 'lease_term',
    listing_value: 'min 12 months',
    measure_detail:
      'The lease duration the applicant intends against the minimum this unit asks for. It carries a weight and does not gate eligibility.',
    weight: 5,
  },
]

const MOCK_MATCH_RULES_AVAILABLE_CRITERIA = [
  { default_weight: 30, key: 'move_in' },
  { default_weight: 25, key: 'budget' },
  { default_weight: 15, key: 'needs' },
  { default_weight: 5, key: 'rooms_area' },
  { default_weight: 5, key: 'lease_term' },
  { default_weight: 10, key: 'temporary_let' },
]

const MOCK_MATCH_RULES_DISTRIBUTION = {
  eligible: 21,
  incomplete: 11,
  not_eligible: 5,
}

export function getMockListingMatchRules() {
  if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noMatching)) {
    return null
  }

  const weightsTotal = MOCK_MATCH_RULES_CRITERIA.reduce(
    (total, criterion) => total + criterion.weight,
    0
  )

  const criteria = MOCK_MATCH_RULES_CRITERIA.map(criterion => ({
    ...criterion,
    on: true,
    weight_pct: Math.round((criterion.weight / weightsTotal) * 100),
  }))

  return {
    affected_applicants_count: 37,
    available_criteria: MOCK_MATCH_RULES_AVAILABLE_CRITERIA,
    budget_is_hard: false,
    can_configure_matching: !hasRentalPortalMockState(
      RENTAL_PORTAL_MOCK_STATE.readOnly
    ),
    config_version: 7,
    criteria,
    current_distribution: MOCK_MATCH_RULES_DISTRIBUTION,
    max_occupancy: { source: 'listing', value: 4 },
    min_occupancy: null,
    pets_policy: 'not_allowed',
    rules_changed_at: '2026-09-02T12:12:00Z',
    score_enabled: true,
    scored_criteria_count: criteria.filter(item => item.weight > 0).length,
    summary_line:
      'Five scored criteria, four gates · last changed 02/09/2026 by the company catalogue',
    wbs_required: false,
    weights_total: weightsTotal,
  }
}

const MOCK_MATCH_RULES_PREVIEW_DELAY = 350

// TODO: BE #10a `POST …/matching-rules/preview/` does not exist yet
export function postMockListingMatchRulesPreview({ criteria }) {
  const rules = getMockListingMatchRules()

  const baselineCount = rules.criteria.filter(
    item => !item.gates_eligibility
  ).length

  const shift = criteria.length - baselineCount

  const {
    eligible,
    incomplete,
    not_eligible: notEligible,
  } = rules.current_distribution

  return new Promise(resolve => {
    setTimeout(
      () =>
        resolve({
          eligible: Math.max(eligible + shift, 0),
          incomplete: Math.max(incomplete - shift, 0),
          not_eligible: notEligible,
        }),
      MOCK_MATCH_RULES_PREVIEW_DELAY
    )
  })
}

const MOCK_MATCH_RULES_SAVE_DELAY = 500

const MOCK_MATCH_RULES_SCORED_MIN = 3

// TODO: BE #10 `PATCH …/matching-rules/` does not exist yet
export function patchMockListingMatchRules({ values }) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const scoredCount = values.criteria.filter(
        key => Number(values.weights[key]) > 0
      ).length

      if (scoredCount < MOCK_MATCH_RULES_SCORED_MIN) {
        reject({ code: 'insufficient_scored_criteria' })

        return
      }

      resolve({ data: { job_id: startMockMatchReevaluationJob() } })
    }, MOCK_MATCH_RULES_SAVE_DELAY)
  })
}

const MOCK_MATCH_JOB_DURATION = 4000

const MOCK_MATCH_JOB_STATE = { done: 'done', running: 'running' }

const MOCK_MATCH_JOB_FINISH_AT = new Map()

function startMockMatchReevaluationJob() {
  const jobId = `reevaluation-${MOCK_MATCH_JOB_FINISH_AT.size + 1}`

  MOCK_MATCH_JOB_FINISH_AT.set(jobId, Date.now() + MOCK_MATCH_JOB_DURATION)

  return jobId
}

export function getMockMatchReevaluationJob(jobId) {
  const finishAt = MOCK_MATCH_JOB_FINISH_AT.get(jobId)

  return Promise.resolve({
    data: {
      state:
        !finishAt || Date.now() >= finishAt
          ? MOCK_MATCH_JOB_STATE.done
          : MOCK_MATCH_JOB_STATE.running,
    },
  })
}

export function getIsMockMatchJobDone(job) {
  return job?.data?.state === MOCK_MATCH_JOB_STATE.done
}
